import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BaseApiService } from '@app/services/api/base.service';
import { isDevMode } from '@angular/core';
import { SurveyResponse } from '@app/schemas/surveys';
import { catchError } from 'rxjs/operators';
import { of, throwError } from 'rxjs';

interface ResponseAPIParams {
  survey_id: string;
  responses: SurveyResponse[];
}

@Injectable({
  providedIn: 'root'
})
export class SurveysService extends BaseApiService {
  private cachedRiasec$: Observable<any> | null = null;
  private cachedRiasecValue: any = null;

  constructor(http: HttpClient) {
    const apiBaseUrl = isDevMode() ? '/surveys-api' : environment.surveysApiUrl;

    super(http, apiBaseUrl);
  }

  getSurvey(uuid: string): Observable<any> {
    return this.get<any>(`surveys/${uuid}`);
  }

  saveUserSurveyResponse(data: ResponseAPIParams): Observable<any> {
    return this.post<any>('user-survey-response', data);
  }

  // Returns null if no RIASEC score found for the user
  getUserRiasecScore(surveyId: string): Observable<any> {
    if (!this.cachedRiasec$) {
      this.cachedRiasec$ = this.get<any>(`surveys/${surveyId}/user-riasec-score`).pipe(
        // If the API returns a 404 (not found), we return null instead of throwing an error
        // This is because a 404 means the user has not completed the survey yet
        // and we want to handle that case gracefully
        catchError((error) => {
          if (error.status === 404) {
            return of(null);
          } else {
            return throwError(() => error);
          }
        }),
        tap(data => this.cachedRiasecValue = data),
        shareReplay(1),
      );
    }
    return this.cachedRiasec$;
  }

  // Returns the cached RIASEC score synchronously, or null if not yet fetched. */
  getCachedRiasecScore(): any {
    return this.cachedRiasecValue;
  }

  // Sets the cached RIASEC score directly (e.g. after survey completion). */
  setCachedRiasecScore(score: any): void {
    this.cachedRiasecValue = score;
    // Create a new cached observable that emits the provided score
    this.cachedRiasec$ = of({ riasec_score: score }).pipe(shareReplay(1));
  }

  // Clears the cached RIASEC data so the next call fetches fresh from the API. */
  invalidateRiasecCache(): void {
    this.cachedRiasec$ = null;
    this.cachedRiasecValue = null;
  }
}
